import { createTheme } from '@mui/material/styles';

const lightPalette = {
  primary: {
    main: '#0F172A', // Slate 900
    light: '#334155',
    dark: '#020617',
    contrastText: '#F8FAFC',
  },
  secondary: {
    main: '#6366F1', // Indigo 500
    light: '#818CF8',
    dark: '#4f46e5',
    contrastText: '#ffffff',
  },
  success: { main: '#0D9488', light: '#14B8A6', dark: '#0F766E' },
  warning: { main: '#F59E0B', light: '#FBBF24', dark: '#D97706' },
  error: { main: '#EF4444', light: '#F87171', dark: '#B91C1C' },
  background: {
    default: '#F1F5F9', // Clean light background
    paper: '#FFFFFF',
    navbar: 'rgba(255, 255, 255, 0.8)',
  },
  text: {
    primary: '#1E293B',
    secondary: '#64748B',
    navbar: '#1E293B',
  },
  divider: 'rgba(226, 232, 240, 0.8)',
};

const darkPalette = {
  primary: {
    main: '#6366F1', // Indigo accent for primary elements
    light: '#818CF8',
    dark: '#4f46e5',
    contrastText: '#F3F4F6',
  },
  secondary: {
    main: '#8b5cf6', // Violet 500
    light: '#a78bfa',
    dark: '#7c3aed',
    contrastText: '#ffffff',
  },
  success: { main: '#0D9488', light: '#14B8A6', dark: '#0F766E' },
  warning: { main: '#F59E0B', light: '#FBBF24', dark: '#D97706' },
  error: { main: '#EF4444', light: '#F87171', dark: '#B91C1C' },
  background: {
    default: '#111827', // dark bg from login.html
    paper: '#1F2937',   // dark surface from login.html
    navbar: 'rgba(31, 41, 55, 0.8)', 
  },
  text: {
    primary: '#F3F4F6', // text from login.html
    secondary: '#9CA3AF', // textMuted from login.html
    navbar: '#F3F4F6',
  },
  divider: '#374151', // border from login.html
};

export const getAppTheme = (mode) => createTheme({
  palette: {
    mode,
    ...(mode === 'light' ? lightPalette : darkPalette),
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica Neue", Arial, sans-serif',
    h1: { fontWeight: 700, letterSpacing: '-0.025em' },
    h2: { fontWeight: 700, letterSpacing: '-0.025em' },
    h3: { fontWeight: 600, letterSpacing: '-0.025em' },
    h4: { fontWeight: 600 },
    h5: { fontWeight: 600 },
    h6: { fontWeight: 600 },
    button: { textTransform: 'none', fontWeight: 600 },
  },
  shape: {
    borderRadius: 6, // Clean UI, sharper corners
  },
  shadows: [
    'none',
    '0 1px 2px 0 rgb(0 0 0 / 0.05)',
    '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)', // Soft flat shadow
    '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
    '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
    // ... rest standard or customized
    ...Array(20).fill('none'), // Fill standard MUI shadows array
  ],
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: '#F1F5F9',
          scrollBehavior: 'smooth',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: "rgba(255, 255, 255, 0.85) !important", // Glassmorphism force
          backdropFilter: 'blur(12px)',
          borderBottom: '1px solid rgba(226, 232, 240, 0.8)',
          color: '#1E293B',
          boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.05)',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          padding: '8px 24px',
          boxShadow: 'none',
          transition: 'all 0.2s ease-in-out',
          fontWeight: 600,
          '&:hover': {
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
          },
          '&:active': {
            transform: 'none',
            boxShadow: 'none',
          }
        },
        contained: {
          '&:hover': {
            boxShadow: '0 12px 20px -3px rgba(0, 0, 0, 0.15)',
          },
        },
        outlined: {
          borderWidth: '2px',
          '&:hover': {
            borderWidth: '2px',
          }
        }
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
          border: '1px solid rgba(226, 232, 240, 0.8)',
          borderRadius: 8,
          transition: 'box-shadow 0.2s',
        },
        elevation1: {
          boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1)',
        },
        elevation2: {
          boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1)',
          border: '1px solid rgba(226, 232, 240, 0.8)',
          transition: 'all 0.2s',
          '&:hover': {
            boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
            borderColor: 'rgba(99, 102, 241, 0.3)',
          }
        }
      }
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: '1px solid #E2E8F0',
          padding: '16px',
        },
        head: {
          backgroundColor: '#F8FAFC',
          color: '#475569',
          fontWeight: 600,
          textTransform: 'uppercase',
          fontSize: '0.75rem',
          letterSpacing: '0.05em',
          borderBottom: '1px solid #E2E8F0',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          transition: 'background-color 0.2s',
          '&:hover': {
            backgroundColor: '#F1F5F9 !important',
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 500,
          borderRadius: 4,
          transition: 'background-color 0.2s',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.08)',
          }
        },
      },
    },
  },
});